import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import { Typography } from '@/shared/components/ui/typography'
import { useMoneyFormatter } from '@/shared/hooks/useFormatters'
import { usePlans } from '@/shared/hooks/usePlans'
import { cn } from '@/shared/lib/utils'
import { ComponentProps } from 'react'

type PlanSelectorProps = ComponentProps<'div'> & {
  currentPlanId?: string
  onPlanChange?: (planId: string) => void
  isLoading?: boolean
  disabled?: boolean
}

export const PlanSelector = ({
  currentPlanId,
  onPlanChange,
  isLoading,
  disabled,
  ...props
}: PlanSelectorProps) => {
  const { formatCurrencyValue } = useMoneyFormatter()
  const plans = usePlans()

  return (
    <div
      {...props}
      className={cn(
        'flex w-fit flex-col flex-wrap items-start gap-6 md:w-auto md:flex-row',
        props.className
      )}
    >
      {plans.map((plan) => {
        const isCurrentPlan = plan.id === currentPlanId
        const isDisabled =
          disabled || isLoading || isCurrentPlan || !plan.isEnabled

        return (
          <Card
            key={plan.id}
            className={cn(
              'relative w-full md:max-w-80',
              isCurrentPlan
                ? 'border-secondary bg-secondary-light/20'
                : !plan.isEnabled
                  ? 'opacity-50'
                  : ''
            )}
          >
            <CardHeader className="pb-4 text-left">
              <div className="flex flex-col gap-1">
                <Typography className="text-sm font-semibold">
                  {plan.name}
                </Typography>
                <p
                  className={cn(
                    'text-xs',
                    isCurrentPlan ? 'text-secondary' : 'text-mydra-gray'
                  )}
                >
                  {plan.description}
                </p>
                <div className="text-2xl font-bold">
                  {formatCurrencyValue(
                    plan.price.amount.toString(),
                    plan.price.currency
                  )}
                  {plan.isMonthlyPlan && (
                    <span className="text-sm font-normal text-gray-500">
                      {' '}
                      / user billed monthly
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <div className="flex flex-col gap-4">
                <Button
                  variant={isCurrentPlan ? 'outline' : 'default'}
                  className={cn(
                    'w-full',
                    !isCurrentPlan
                      ? 'bg-black text-white hover:bg-gray-800'
                      : '',
                    isCurrentPlan
                      ? 'border-secondary-light bg-secondary-light text-black !opacity-100 hover:bg-secondary-light/90'
                      : ''
                  )}
                  disabled={isDisabled}
                  onClick={() => {
                    if (
                      isCurrentPlan ||
                      disabled ||
                      isLoading ||
                      !plan.isEnabled
                    )
                      return
                    onPlanChange?.(plan.id)
                  }}
                >
                  {isLoading && !isCurrentPlan
                    ? 'Loading...'
                    : isCurrentPlan
                      ? 'This is your current plan'
                      : !plan.isEnabled
                        ? 'Coming Soon'
                        : `Get ${plan.name} plan`}
                </Button>

                <ul className="flex list-inside list-disc flex-col gap-2 text-sm text-black">
                  {plan.features.map((feature, index) => (
                    <li
                      key={index}
                      className={feature.highlight ? 'font-medium' : ''}
                    >
                      {feature.text}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}
